import { MagnifyingGlassIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { useMemo, useState, useEffect, useRef } from "react";
import { PublicUserOrganization, UserGroupFragment } from "../../__generated__/graphql";
import Button from "../Button";
import Modal from "../Modal";
import UserOrganizationItem from "../UserOrganizationItem";
import useOrganizationMembers from "../../hooks/useOrganizationMembers";
import useOrganizationSharings from "../../hooks/useOrganizationSharings";
import { useQuery } from "@apollo/client";
import { UserGroupsDocument } from "../../__generated__/graphql";
import Avatar from "../Avatar";

const removeAccents = (str: string) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export interface ParticipantsAndGroupsSelection {
  users: PublicUserOrganization[];
  groups: UserGroupFragment[];
}

interface ParticipantsSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selectedUsers: PublicUserOrganization[], selectedGroups?: UserGroupFragment[]) => void;
  title?: string;
  description?: string;
  initialSelectedUsers?: PublicUserOrganization[];
  initialSelectedGroups?: UserGroupFragment[];
  submitButtonText?: string;
  showGroups?: boolean;
  projectId?: string;
}

type TabType = "participants" | "groups";

export default function ParticipantsSelectionModal({
  isOpen,
  onClose,
  onSubmit,
  title = "Sélectionner des participants",
  description = "Sélectionnez les participants parmi les membres de votre organisation et vos collaborateurs externes.",
  initialSelectedUsers = [],
  initialSelectedGroups = [],
  submitButtonText = "Confirmer la sélection",
  showGroups = false,
  projectId,
}: ParticipantsSelectionModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<TabType>("participants");
  const [selectedUsers, setSelectedUsers] = useState<PublicUserOrganization[]>(
    initialSelectedUsers
  );
  const [selectedGroups, setSelectedGroups] = useState<UserGroupFragment[]>(
    initialSelectedGroups
  );
  const wasOpenRef = useRef(false);

  const { data: organizationMembersData, loading: loadingMembers } =
    useOrganizationMembers();

  const { externalUserOrganizations, loading: loadingSharings } =
    useOrganizationSharings();

  const { data: userGroupsData, loading: loadingGroups } = useQuery(
    UserGroupsDocument,
    { skip: !showGroups }
  );

  // Reset state only when modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal just opened
      setSearchQuery("");
      setSelectedUsers(initialSelectedUsers);
      setSelectedGroups(initialSelectedGroups);
      setActiveTab("participants");
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialSelectedUsers, initialSelectedGroups]);

  // Filter groups based on projectId - show project-scoped and organization-wide groups
  const availableGroups = useMemo(() => {
    if (!userGroupsData?.userGroups) return [];
    
    return userGroupsData.userGroups.filter((group) => {
      // If projectId is provided, show groups scoped to this project or organization-wide groups
      if (projectId) {
        return group.projectId === projectId || (!group.projectId && !group.eventId);
      }
      // Otherwise show all groups
      return true;
    });
  }, [userGroupsData, projectId]);

  // Format organization members as PublicUserOrganization
  const organizationMembers: PublicUserOrganization[] = useMemo(() => {
    return (
      organizationMembersData?.organizationMembers?.filter(
        (member) => member !== undefined && member !== null
      ) ?? []
    ).map((member) => ({
      id: member.id,
      name: member.name,
      organizationName: member.organizationName,
      organizationId: member.organizationId,
      organizationType: member.organizationType,
      email: member.email,
      phoneNumber: member.phoneNumber,
      avatarBucketKey: member.avatarBucketKey,
      jobTitle: member.jobTitle,
    }));
  }, [organizationMembersData]);

  // Format external users as PublicUserOrganization
  const externalUsers: PublicUserOrganization[] = useMemo(() => {
    return (
      externalUserOrganizations?.filter(
        (user): user is PublicUserOrganization => user !== null && user !== undefined
      ) ?? []
    ).map((user) => ({
      id: user.id,
      name: user.name,
      organizationName: user.organizationName,
      organizationId: user.organizationId,
      organizationType: user.organizationType,
      email: user.email,
      phoneNumber: user.phoneNumber,
      avatarBucketKey: user.avatarBucketKey,
      jobTitle: user.jobTitle,
    }));
  }, [externalUserOrganizations]);

  // Filter users based on search query
  const filteredOrganizationMembers = useMemo(() => {
    if (!searchQuery) return organizationMembers;

    const normalizedQuery = removeAccents(searchQuery.toLowerCase());
    return organizationMembers.filter((member) => {
      const name = removeAccents(member.name.toLowerCase());
      const orgName = removeAccents(member.organizationName.toLowerCase());
      const email = removeAccents((member.email ?? "").toLowerCase());
      const jobTitle = removeAccents((member.jobTitle ?? "").toLowerCase());

      return (
        name.includes(normalizedQuery) ||
        orgName.includes(normalizedQuery) ||
        email.includes(normalizedQuery) ||
        jobTitle.includes(normalizedQuery)
      );
    });
  }, [organizationMembers, searchQuery]);

  const filteredExternalUsers = useMemo(() => {
    if (!searchQuery) return externalUsers;

    const normalizedQuery = removeAccents(searchQuery.toLowerCase());
    return externalUsers.filter((user) => {
      const name = removeAccents(user.name.toLowerCase());
      const orgName = removeAccents(user.organizationName.toLowerCase());
      const email = removeAccents((user.email ?? "").toLowerCase());
      const jobTitle = removeAccents((user.jobTitle ?? "").toLowerCase());

      return (
        name.includes(normalizedQuery) ||
        orgName.includes(normalizedQuery) ||
        email.includes(normalizedQuery) ||
        jobTitle.includes(normalizedQuery)
      );
    });
  }, [externalUsers, searchQuery]);

  // Filter groups based on search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return availableGroups;

    const normalizedQuery = removeAccents(searchQuery.toLowerCase());
    return availableGroups.filter((group) => {
      const name = removeAccents(group.name.toLowerCase());
      const description = removeAccents((group.description ?? "").toLowerCase());

      return name.includes(normalizedQuery) || description.includes(normalizedQuery);
    });
  }, [availableGroups, searchQuery]);

  const toggleUser = (user: PublicUserOrganization) => {
    if (selectedUsers.some((u) => u.id === user.id)) {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const toggleGroup = (group: UserGroupFragment) => {
    if (selectedGroups.some((g) => g.id === group.id)) {
      setSelectedGroups(selectedGroups.filter((g) => g.id !== group.id));
    } else {
      setSelectedGroups([...selectedGroups, group]);
    }
  };

  const handleSubmit = () => {
    onSubmit(selectedUsers, showGroups ? selectedGroups : undefined);
    onClose();
  };

  const isLoading = loadingMembers || loadingSharings || loadingGroups;
  const totalUsers =
    filteredOrganizationMembers.length + filteredExternalUsers.length;
  const hasExternalUsers = externalUsers.length > 0;
  const totalSelections = selectedUsers.length + selectedGroups.length;

  return (
    <Modal
      dataTestId="participants-modal"
      isModalOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      className="w-2xl max-w-[90vw]"
      dialogClassName="z-[350]"
    >
      <div className="flex flex-col gap-4">
        {/* Tabs */}
        {showGroups && (
          <div className="flex border-b border-border">
            <button
              data-testid="participants-tab-participants"
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "participants"
                  ? "text-accent border-b-2 border-accent"
                  : "text-secondary hover:text-primary"
              }`}
              onClick={() => setActiveTab("participants")}
            >
              Participants
              {selectedUsers.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-accent/20 text-accent rounded-full">
                  {selectedUsers.length}
                </span>
              )}
            </button>
            <button
              data-testid="participants-tab-groups"
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "groups"
                  ? "text-accent border-b-2 border-accent"
                  : "text-secondary hover:text-primary"
              }`}
              onClick={() => setActiveTab("groups")}
            >
              Groupes
              {selectedGroups.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-accent/20 text-accent rounded-full">
                  {selectedGroups.length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Search Input */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tertiary" />
          <input
            data-testid="participants-search-input"
            type="text"
            placeholder={
              activeTab === "participants"
                ? "Rechercher par nom, organisation, email..."
                : "Rechercher un groupe..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent placeholder:text-tertiary"
            autoFocus
          />
        </div>

        {/* Selection Counter */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-secondary">
            {activeTab === "participants" ? (
              <>
                {totalUsers} participant{totalUsers !== 1 ? "s" : ""} trouvé
                {totalUsers !== 1 ? "s" : ""}
              </>
            ) : (
              <>
                {filteredGroups.length} groupe{filteredGroups.length !== 1 ? "s" : ""} trouvé
                {filteredGroups.length !== 1 ? "s" : ""}
              </>
            )}
          </span>
          <span className="text-accent font-medium">
            {totalSelections} sélectionné
            {totalSelections > 1 ? "s" : ""}
          </span>
        </div>

        {/* Content */}
        <div className="max-h-[400px] overflow-y-auto custom-scrollbar space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-tertiary">
                Chargement...
              </div>
            </div>
          ) : activeTab === "participants" ? (
            /* Participants Tab */
            totalUsers === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-tertiary">
                  {searchQuery
                    ? "Aucun participant trouvé pour cette recherche"
                    : "Aucun participant disponible"}
                </div>
              </div>
            ) : (
              <>
                {/* External Users Section */}
                {hasExternalUsers && filteredExternalUsers.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-secondary uppercase tracking-wider mb-2">
                      Collaborateurs externes ({filteredExternalUsers.length})
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {filteredExternalUsers.map((user) => (
                        <UserOrganizationItem
                          key={user.id}
                          userOrganization={user}
                          selected={selectedUsers.some((u) => u.id === user.id)}
                          onClick={toggleUser}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Organization Members Section */}
                {filteredOrganizationMembers.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-secondary uppercase tracking-wider mb-2">
                      Membres de votre organisation ({filteredOrganizationMembers.length})
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {filteredOrganizationMembers.map((member) => (
                        <UserOrganizationItem
                          key={member.id}
                          userOrganization={member}
                          selected={selectedUsers.some((u) => u.id === member.id)}
                          onClick={toggleUser}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )
          ) : (
            /* Groups Tab */
            filteredGroups.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-tertiary">
                  {searchQuery
                    ? "Aucun groupe trouvé pour cette recherche"
                    : "Aucun groupe disponible"}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {filteredGroups.map((group) => {
                  const isSelected = selectedGroups.some((g) => g.id === group.id);
                  return (
                    <div
                      key={group.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? "border-accent bg-accent/10"
                          : "border-border bg-card hover:border-accent/50"
                      }`}
                      onClick={() => toggleGroup(group)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isSelected ? "bg-accent/20" : "bg-border"
                        }`}>
                          <UserGroupIcon className={`w-5 h-5 ${
                            isSelected ? "text-accent" : "text-secondary"
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-primary truncate">
                            {group.name}
                          </div>
                          <div className="text-xs text-secondary">
                            {group.members.length} membre{group.members.length > 1 ? "s" : ""}
                            {group.projectId && " · Groupe du projet"}
                          </div>
                        </div>
                        {/* Member avatars preview */}
                        {group.members.length > 0 && (
                          <div className="flex -space-x-2">
                            {group.members.slice(0, 3).map((member) => (
                              <Avatar
                                key={member.id}
                                userOrganization={member.publicUserOrganization}
                                size="xs"
                                className="ring-2 ring-card"
                              />
                            ))}
                            {group.members.length > 3 && (
                              <div className="w-6 h-6 rounded-full bg-border flex items-center justify-center text-xs text-secondary ring-2 ring-card">
                                +{group.members.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {group.description && (
                        <div className="mt-2 text-xs text-tertiary truncate">
                          {group.description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        {/* Selection Summary */}
        {(selectedUsers.length > 0 || selectedGroups.length > 0) && (
          <div className="p-3 bg-card rounded-lg border border-border">
            <div className="text-xs font-medium text-secondary uppercase tracking-wider mb-2">
              Sélection
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedGroups.map((group) => (
                <div
                  key={group.id}
                  className="flex items-center gap-1 px-2 py-1 bg-accent/20 text-accent rounded-full text-xs"
                >
                  <UserGroupIcon className="w-3 h-3" />
                  {group.name}
                  <button
                    className="ml-1 hover:text-accent/70"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroup(group);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              {selectedUsers.slice(0, 5).map((user) => (
                <div
                  key={user.id}
                  className="flex items-center gap-1 px-2 py-1 bg-border rounded-full text-xs"
                >
                  <Avatar userOrganization={user} size="xs" />
                  <span className="text-primary truncate max-w-[100px]">{user.name}</span>
                  <button
                    className="ml-1 text-secondary hover:text-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleUser(user);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              {selectedUsers.length > 5 && (
                <div className="flex items-center text-xs text-tertiary px-2">
                  +{selectedUsers.length - 5} autres
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center pt-4 border-t border-border justify-end gap-2">
          <Button dataTestId="participants-cancel-button" variant="secondary" type="button" onClick={onClose}>
            Annuler
          </Button>
          <Button dataTestId="participants-submit-button" type="button" onClick={handleSubmit}>
            {submitButtonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
